import { useRef, useEffect, forwardRef, useImperativeHandle } from "react";

interface GameIframeProps {
  gamePath: string; // 예: "/games/my-game/index.html"
  title: string;
  onGameEvent?: (event: any) => void;
  className?: string;
}

export interface GameIframeRef {
  sendMessageToGame: (message: any) => void;
}

export const GameIframe = forwardRef<GameIframeRef, GameIframeProps>(
  (
    { gamePath, title, onGameEvent, className = "w-full h-full border-none" },
    ref,
  ) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);

    // 게임에 메시지 보내기
    const sendMessageToGame = (message: any) => {
      if (iframeRef.current) {
        iframeRef.current.contentWindow?.postMessage(message, "*");
      }
    };

    // ref를 통해 외부에서 접근할 수 있는 메서드들 노출
    useImperativeHandle(ref, () => ({
      sendMessageToGame,
    }));

    useEffect(() => {
      const handleMessage = (event: MessageEvent) => {
        // iframe에서 온 메시지인지 확인
        if (event.source !== iframeRef.current?.contentWindow) {
          return;
        }

        // 부모 컴포넌트에 이벤트 전달
        if (onGameEvent) {
          onGameEvent(event.data);
        }
      };

      window.addEventListener("message", handleMessage);
      return () => window.removeEventListener("message", handleMessage);
    }, [title, onGameEvent]);

    return (
      <iframe
        ref={iframeRef}
        src={gamePath}
        className={className}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  },
);

GameIframe.displayName = "GameIframe";
